"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ACTIVITY_CONFIG = exports.UNIT_CATEGORIES = void 0;
exports.getStationaryFuels = getStationaryFuels;
exports.getMobileFuels = getMobileFuels;
exports.getValidUnits = getValidUnits;
const enums_1 = require("./enums");
const UNIT_WEIGHT = [enums_1.ActivityUnit.Kilograms, enums_1.ActivityUnit.Tonnes];
const UNIT_ENERGY = [enums_1.ActivityUnit.KWh, enums_1.ActivityUnit.MWh, enums_1.ActivityUnit.GJ, enums_1.ActivityUnit.MJ];
const UNIT_DISTANCE = [enums_1.ActivityUnit.Kilometers, enums_1.ActivityUnit.Miles];
const UNIT_VOLUME = [enums_1.ActivityUnit.Litres, enums_1.ActivityUnit.Gallons, enums_1.ActivityUnit.CubicMeters, enums_1.ActivityUnit.CubicFeet];
const UNIT_AREA = [enums_1.ActivityUnit.SquareMeters, enums_1.ActivityUnit.SquareFeet];
exports.UNIT_CATEGORIES = {
    ...Object.fromEntries(UNIT_VOLUME.map((u) => [u, 'Volume'])),
    ...Object.fromEntries(UNIT_WEIGHT.map((u) => [u, 'Weight'])),
    ...Object.fromEntries(UNIT_ENERGY.map((u) => [u, 'Energy'])),
    ...Object.fromEntries(UNIT_DISTANCE.map((u) => [u, 'Distance'])),
    ...Object.fromEntries(UNIT_AREA.map((u) => [u, 'Area'])),
    [enums_1.ActivityUnit.Each]: 'Count',
};
/**
 * Valid fuel types per vehicle type and scope for mobile combustion.
 * This is the single source of truth for vehicle+fuel+scope combinations — used by
 * frontend dropdowns, backend validation, and the coverage script test matrix.
 *
 * Scope 3 entries mirror Scope 1 for now — to be refined when Scope 3 form is built.
 * Biodiesel is absent from Scope 1: Climatiq only has biodiesel vehicle factors under
 * Scope 3 lifecycle categories, not Scope 1 combustion.
 */
const BASE_FUELS = [enums_1.FuelType.Petrol, enums_1.FuelType.Diesel, enums_1.FuelType.Electric, enums_1.FuelType.PlugInHybrid];
const DEFAULT_FUELS = [...BASE_FUELS, enums_1.FuelType.Hybrid, enums_1.FuelType.LPG, enums_1.FuelType.CNG, enums_1.FuelType.Unknown];
const DEFAULT_FUELS_SCOPE_3 = [...BASE_FUELS, enums_1.FuelType.Hybrid, enums_1.FuelType.LPG, enums_1.FuelType.CNG, enums_1.FuelType.Hydrogen, enums_1.FuelType.Unknown];
const FUELS_BY_VEHICLE_TYPE = {
    [enums_1.VehicleType.Car]: {
        [enums_1.EmissionScope.Scope1]: DEFAULT_FUELS,
        [enums_1.EmissionScope.Scope3]: DEFAULT_FUELS_SCOPE_3,
    },
    [enums_1.VehicleType.SmallCar]: {
        [enums_1.EmissionScope.Scope1]: [...BASE_FUELS, enums_1.FuelType.Hybrid],
        [enums_1.EmissionScope.Scope3]: DEFAULT_FUELS_SCOPE_3,
    },
    [enums_1.VehicleType.MediumCar]: {
        [enums_1.EmissionScope.Scope1]: [...BASE_FUELS, enums_1.FuelType.Hybrid, enums_1.FuelType.LPG, enums_1.FuelType.CNG],
        [enums_1.EmissionScope.Scope3]: DEFAULT_FUELS_SCOPE_3,
    },
    [enums_1.VehicleType.LargeCar]: {
        [enums_1.EmissionScope.Scope1]: [...BASE_FUELS, enums_1.FuelType.Hybrid, enums_1.FuelType.LPG, enums_1.FuelType.CNG],
        [enums_1.EmissionScope.Scope3]: DEFAULT_FUELS_SCOPE_3,
    },
    [enums_1.VehicleType.Van]: {
        [enums_1.EmissionScope.Scope1]: [...BASE_FUELS, enums_1.FuelType.LPG, enums_1.FuelType.CNG],
        [enums_1.EmissionScope.Scope3]: DEFAULT_FUELS_SCOPE_3,
    },
    [enums_1.VehicleType.HGV]: {
        [enums_1.EmissionScope.Scope1]: [enums_1.FuelType.Diesel],
        [enums_1.EmissionScope.Scope3]: [enums_1.FuelType.Diesel, enums_1.FuelType.LNG, enums_1.FuelType.Hydrogen, enums_1.FuelType.Unknown],
    },
    [enums_1.VehicleType.Motorcycle]: {
        [enums_1.EmissionScope.Scope1]: [enums_1.FuelType.Petrol, enums_1.FuelType.Diesel, enums_1.FuelType.Unknown],
        [enums_1.EmissionScope.Scope3]: [enums_1.FuelType.Petrol, enums_1.FuelType.Diesel, enums_1.FuelType.Unknown],
    },
};
exports.ACTIVITY_CONFIG = {
    unitsByActivityType: {
        [enums_1.Scope1ActivityType.StationaryCombustion]: [...UNIT_VOLUME, ...UNIT_WEIGHT, ...UNIT_ENERGY],
        [enums_1.Scope1ActivityType.MobileCombustion]: [...UNIT_VOLUME, ...UNIT_DISTANCE],
        [enums_1.Scope1ActivityType.FugitiveEmissions]: UNIT_WEIGHT,
        [enums_1.Scope1ActivityType.ProcessEmissions]: UNIT_WEIGHT,
        [enums_1.Scope2ActivityType.PurchasedElectricity]: UNIT_ENERGY,
        [enums_1.Scope2ActivityType.PurchasedHeating]: UNIT_ENERGY,
        [enums_1.Scope2ActivityType.PurchasedSteam]: UNIT_ENERGY,
        [enums_1.Scope2ActivityType.PurchasedCooling]: UNIT_ENERGY,
    },
    fuelTypesByActivityType: {
        [enums_1.Scope1ActivityType.StationaryCombustion]: [
            enums_1.FuelType.NaturalGas,
            enums_1.FuelType.LPG,
            enums_1.FuelType.Propane,
            enums_1.FuelType.Butane,
            enums_1.FuelType.CNG,
            enums_1.FuelType.LNG,
            enums_1.FuelType.Diesel,
            enums_1.FuelType.Petrol,
            enums_1.FuelType.HeavyFuelOil,
            enums_1.FuelType.LightFuelOil,
            enums_1.FuelType.Kerosene,
            enums_1.FuelType.JetFuel,
            enums_1.FuelType.Ethanol,
            enums_1.FuelType.HVO,
            enums_1.FuelType.Coal,
            enums_1.FuelType.CoalIndustrial,
            enums_1.FuelType.CoalDomestic,
            enums_1.FuelType.Anthracite,
            enums_1.FuelType.Coke,
            enums_1.FuelType.Wood,
            enums_1.FuelType.WoodLogs,
            enums_1.FuelType.WoodChips,
            enums_1.FuelType.WoodPellets,
            enums_1.FuelType.Biomass,
            enums_1.FuelType.Hydrogen,
            // FuelType.Unknown, // Does this make sense to offer if it will just fail?
        ],
        [enums_1.Scope1ActivityType.MobileCombustion]: [
            enums_1.FuelType.Petrol,
            enums_1.FuelType.Diesel,
            enums_1.FuelType.LPG,
            enums_1.FuelType.Biodiesel,
            enums_1.FuelType.Electric,
            enums_1.FuelType.PlugInHybrid,
            enums_1.FuelType.Hybrid,
            enums_1.FuelType.CNG,
            enums_1.FuelType.LNG,
            enums_1.FuelType.Hydrogen,
            // FuelType.Unknown, // Does this make sense to offer if it will just fail?
        ],
    },
    fuelsByVehicleType: FUELS_BY_VEHICLE_TYPE,
    /**
     * Unit restrictions for specific fuel types.
     * When a fuel type appears here, only the listed units are valid — regardless
     * of the activity type's default unit list.
     */
    unitsByFuelType: {
        // Electric/PHEV/HEV: distance-based Climatiq factors only
        [enums_1.FuelType.Electric]: UNIT_DISTANCE,
        [enums_1.FuelType.PlugInHybrid]: UNIT_DISTANCE,
        [enums_1.FuelType.Hybrid]: UNIT_DISTANCE,
        // Solid fuels: weight or energy only — no volume, no distance
        [enums_1.FuelType.Coal]: [...UNIT_WEIGHT, ...UNIT_ENERGY],
        [enums_1.FuelType.CoalIndustrial]: [...UNIT_WEIGHT, ...UNIT_ENERGY],
        [enums_1.FuelType.CoalDomestic]: [...UNIT_WEIGHT, ...UNIT_ENERGY],
        [enums_1.FuelType.Anthracite]: [...UNIT_WEIGHT, ...UNIT_ENERGY],
        [enums_1.FuelType.Coke]: [...UNIT_WEIGHT, ...UNIT_ENERGY],
        [enums_1.FuelType.Wood]: [...UNIT_WEIGHT, ...UNIT_ENERGY],
        [enums_1.FuelType.WoodLogs]: [...UNIT_WEIGHT, ...UNIT_ENERGY],
        [enums_1.FuelType.WoodChips]: [...UNIT_WEIGHT, ...UNIT_ENERGY],
        [enums_1.FuelType.WoodPellets]: [...UNIT_WEIGHT, ...UNIT_ENERGY],
        [enums_1.FuelType.Biomass]: [...UNIT_WEIGHT, ...UNIT_ENERGY],
    },
};
/**
 * Returns the valid fuel types for stationary combustion (boilers, furnaces, generators).
 * These are fundamentally different from mobile fuels — e.g. Coal, Wood, Heavy Fuel Oil.
 */
function getStationaryFuels() {
    return exports.ACTIVITY_CONFIG.fuelTypesByActivityType[enums_1.Scope1ActivityType.StationaryCombustion];
}
/**
 * Returns the valid fuel types for mobile combustion (vehicles).
 * When a vehicle type is known, fuels are narrowed further (e.g. HGV → Diesel only).
 */
function getMobileFuels(vehicleType, scope) {
    if (vehicleType && scope) {
        return FUELS_BY_VEHICLE_TYPE[vehicleType]?.[scope]
            ?? exports.ACTIVITY_CONFIG.fuelTypesByActivityType[enums_1.Scope1ActivityType.MobileCombustion];
    }
    return exports.ACTIVITY_CONFIG.fuelTypesByActivityType[enums_1.Scope1ActivityType.MobileCombustion];
}
/**
 * Returns the valid units for a given activity type + optional fuel type combination.
 * Fuel-specific overrides take precedence (e.g. Electric → distance only).
 */
function getValidUnits(activityType, fuelType) {
    if (fuelType) {
        const override = exports.ACTIVITY_CONFIG.unitsByFuelType[fuelType];
        if (override)
            return override;
    }
    return exports.ACTIVITY_CONFIG.unitsByActivityType[activityType] ?? [];
}
